<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import { normalizePlanChangePreview, inferChangeType } from '../planChangeHelpers';

const props = defineProps({
  preview: {
    type: Object,
    default: null,
  },
  targetPlanName: {
    type: String,
    default: '',
  },
  currentPlanName: {
    type: String,
    default: '',
  },
  currentInterval: {
    type: String,
    default: 'monthly',
  },
  targetInterval: {
    type: String,
    default: 'monthly',
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isConfirming: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();
const dialogRef = ref(null);

const normalizedPreview = computed(() => {
  const normalized = normalizePlanChangePreview(props.preview, {
    targetPlanName: props.targetPlanName,
    currentPlanName: props.currentPlanName,
    currentInterval: props.currentInterval,
    targetInterval: props.targetInterval,
  });

  const currentPlan =
    normalized?.currentPlan || props.currentPlanName;
  const targetPlan = normalized?.targetPlan || props.targetPlanName;
  const inferredChangeType = inferChangeType(currentPlan, targetPlan, {
    currentInterval: props.currentInterval,
    targetInterval: props.targetInterval,
  });

  if (!normalized) {
    if (!targetPlan || inferredChangeType === 'change') return null;

    return {
      changeType: inferredChangeType,
      currentPlan,
      targetPlan,
      currency: 'eur',
      amountDueCents: 0,
    };
  }

  if (
    (!normalized.changeType || normalized.changeType === 'change') &&
    inferredChangeType !== 'change'
  ) {
    return { ...normalized, changeType: inferredChangeType };
  }

  return normalized;
});

const isIntervalOnlyChange = computed(
  () =>
    normalizedPreview.value?.currentPlan === normalizedPreview.value?.targetPlan &&
    props.currentInterval !== props.targetInterval
);

const isUpgrade = computed(
  () => normalizedPreview.value?.changeType === 'upgrade'
);
const isDowngrade = computed(
  () => normalizedPreview.value?.changeType === 'downgrade'
);

const formatMoney = cents => {
  if (cents == null) return null;

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: (normalizedPreview.value?.currency || 'eur').toUpperCase(),
    minimumFractionDigits: 2,
  }).format(cents / 100);
};

const effectiveDate = computed(() => {
  if (!normalizedPreview.value?.effectiveAt) return '';

  return format(new Date(normalizedPreview.value.effectiveAt), 'dd MMM yyyy');
});

const title = computed(() => {
  const plan = normalizedPreview.value?.targetPlan || props.targetPlanName;

  if (isIntervalOnlyChange.value && isUpgrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.INTERVAL_UPGRADE_TITLE');
  }

  if (isIntervalOnlyChange.value && isDowngrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.INTERVAL_DOWNGRADE_TITLE');
  }

  if (isUpgrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.UPGRADE_TITLE', { plan });
  }

  if (isDowngrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.DOWNGRADE_TITLE', { plan });
  }

  return t('BILLING_SETTINGS.PLAN_CHANGE.CHANGE_TITLE', { plan });
});

const description = computed(() => {
  const plan = normalizedPreview.value?.targetPlan || props.targetPlanName;

  if (isIntervalOnlyChange.value && isUpgrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.INTERVAL_UPGRADE_DESCRIPTION', {
      plan,
    });
  }

  if (isIntervalOnlyChange.value && isDowngrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.INTERVAL_DOWNGRADE_DESCRIPTION', {
      plan,
      date: effectiveDate.value,
    });
  }

  if (isUpgrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.UPGRADE_DESCRIPTION', { plan });
  }

  if (isDowngrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.DOWNGRADE_DESCRIPTION', {
      plan,
      date: effectiveDate.value,
    });
  }

  return '';
});

const prorationAmount = computed(() =>
  formatMoney(
    normalizedPreview.value?.prorationSummary?.amountDueCents ??
      normalizedPreview.value?.amountDueCents
  )
);

const showProrationBreakdown = computed(
  () =>
    isUpgrade.value &&
    normalizedPreview.value?.prorationSummary &&
    (normalizedPreview.value.prorationSummary.newPlanChargeCents > 0 ||
      normalizedPreview.value.prorationSummary.currentPlanCreditCents > 0)
);

const newPlanChargeAmount = computed(() =>
  formatMoney(normalizedPreview.value?.prorationSummary?.newPlanChargeCents)
);

const currentPlanCreditAmount = computed(() =>
  formatMoney(normalizedPreview.value?.prorationSummary?.currentPlanCreditCents)
);

const showAgentWarning = computed(
  () => isDowngrade.value && normalizedPreview.value?.agents?.over_cap
);

const agentWarning = computed(() => {
  const { consumed, allowed } = normalizedPreview.value?.agents || {};
  if (!consumed || !allowed) return '';

  return t('BILLING_SETTINGS.PLAN_CHANGE.AGENT_OVER_CAP', {
    consumed,
    allowed,
    plan: normalizedPreview.value?.targetPlan || props.targetPlanName,
    date: effectiveDate.value,
  });
});

const confirmLabel = computed(() => {
  if (isUpgrade.value) return t('BILLING_SETTINGS.PLAN_CHANGE.CONFIRM_UPGRADE');
  if (isDowngrade.value) {
    return t('BILLING_SETTINGS.PLAN_CHANGE.CONFIRM_DOWNGRADE');
  }

  return t('BILLING_SETTINGS.PLAN_CHANGE.CONFIRM');
});

const canConfirm = computed(
  () =>
    !props.isLoading &&
    !props.isConfirming &&
    (isUpgrade.value || isDowngrade.value) &&
    !!(normalizedPreview.value?.targetPlan || props.targetPlanName)
);

const open = () => {
  dialogRef.value?.open();
};

const close = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  emit('close');
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="lg"
    :title="title"
    :description="description"
    :confirm-button-label="confirmLabel"
    :cancel-button-label="$t('DIALOG.BUTTONS.CANCEL')"
    :is-loading="isConfirming"
    :disable-confirm-button="!canConfirm"
    @confirm="confirm"
    @close="handleDialogClose"
  >
    <div v-if="isLoading" class="text-sm text-n-slate-11">
      {{ $t('BILLING_SETTINGS.PLAN_CHANGE.LOADING') }}
    </div>

    <div v-else-if="normalizedPreview" class="flex flex-col gap-4">
      <div
        v-if="showProrationBreakdown"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
      >
        <p class="text-sm font-medium text-n-slate-12 mb-3">
          {{ $t('BILLING_SETTINGS.PLAN_CHANGE.PRORATION_TITLE') }}
        </p>

        <div class="flex flex-col gap-2 text-sm">
          <div class="flex items-center justify-between gap-4">
            <span class="text-n-slate-11">
              {{
                $t('BILLING_SETTINGS.PLAN_CHANGE.NEW_PLAN_CHARGE', {
                  plan: normalizedPreview.targetPlan,
                })
              }}
            </span>
            <span class="font-medium text-n-slate-12 shrink-0">
              +{{ newPlanChargeAmount }}
            </span>
          </div>

          <div
            v-if="normalizedPreview.prorationSummary.currentPlanCreditCents"
            class="flex items-center justify-between gap-4"
          >
            <span class="text-n-slate-11">
              {{
                $t('BILLING_SETTINGS.PLAN_CHANGE.CURRENT_PLAN_CREDIT', {
                  plan: normalizedPreview.currentPlan,
                  daysLeft: normalizedPreview.prorationSummary.daysLeft,
                  periodDays: normalizedPreview.prorationSummary.periodDays,
                })
              }}
            </span>
            <span class="font-medium text-n-slate-12 shrink-0">
              −{{ currentPlanCreditAmount }}
            </span>
          </div>

          <div
            class="flex items-center justify-between gap-4 pt-2 border-t border-n-weak"
          >
            <span class="font-medium text-n-slate-12">
              {{ $t('BILLING_SETTINGS.PLAN_CHANGE.TOTAL_DUE_TODAY') }}
            </span>
            <span class="text-xl font-semibold text-n-slate-12 shrink-0">
              {{ prorationAmount }}
            </span>
          </div>
        </div>

        <p class="mt-3 text-sm text-n-slate-11">
          {{ $t('BILLING_SETTINGS.PLAN_CHANGE.PRORATION_NOTE') }}
        </p>
      </div>

      <div
        v-else-if="isUpgrade && prorationAmount"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('BILLING_SETTINGS.PLAN_CHANGE.PRORATION_TITLE') }}
        </p>
        <p class="mt-1 text-2xl font-semibold text-n-slate-12">
          {{ prorationAmount }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ $t('BILLING_SETTINGS.PLAN_CHANGE.PRORATION_NOTE') }}
        </p>
      </div>

      <div
        v-if="isDowngrade"
        class="rounded-lg border border-n-weak bg-n-solid-2 p-4"
      >
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('BILLING_SETTINGS.PLAN_CHANGE.EFFECTIVE_DATE') }}
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{ effectiveDate }}
        </p>
      </div>

      <div
        v-if="showAgentWarning"
        class="rounded-lg border border-n-ruby-8 bg-n-ruby-3 p-4"
      >
        <p class="text-sm text-n-ruby-11">
          {{ agentWarning }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
